// «Реклама» — аналитика: сколько потратили и что это принесло, за любой период и в любом разрезе.
// Расход — из Meta (итог кабинета совпадает с Ads Manager), лиды и апрувы — платформы, связанные с объявлениями
// по меткам. Полоса фильтров (период, кабинет, оффер, состояние, поиск) → итоги со сравнением с прошлым
// периодом → график по дням → таблица в выбранном разрезе. Все цифры считает api (ads-report.ts, формулы —
// @autoaff/mcp/metrics); страница только показывает. Кампании друга владельца в итоги и строки не входят:
// они ниже серым списком, там же — вернуть в учёт.
import { type Period, periodKey, periodLabel, periodPhrase } from '@autoaff/schemas/period';
import { Button, Card, EmptyState, int, plural, Skeleton, toast, usd, when } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowUpDown, Megaphone, PlugZap, Search, SearchX, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type AdsData,
  type AdsState,
  type AdsView,
  adStatus,
  type ForeignCampaign,
  markForeign,
  useAds,
  useIntegrations,
} from '../business';
import {
  FilterBar,
  type FilterOption,
  FilterSelect,
  PeriodPicker,
  ResetFilters,
  usePeriod,
  useRemembered,
} from '../filters';
import { AdsAccounts } from './ads/Accounts';
import { AdsChart } from './ads/AdsChart';
import { type AdsMode, AdsTable, budgetText, MODE_KEYS } from './ads/AdsTable';
import { Breakdowns } from './ads/Breakdowns';
import { phoneSorts, sortOf, sortValue, useColumns, useViewSort } from './ads/columns';
import { AdsKpis } from './ads/Kpis';
import { PageHead } from './common';

const STATES: Array<{ value: AdsState; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'running', label: 'Работают сейчас' },
  { value: 'stopped', label: 'Остановленные' },
];

const upper = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

/** Поиск уходит в api, когда перестали печатать. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AdsPage() {
  const [period, setPeriod] = usePeriod('ads', '7d');
  const [mode, setView] = useRemembered<AdsMode>('ads:view', 'campaigns', MODE_KEYS);
  // «Разрезы» — свой запрос (Breakdowns.tsx); итоги и график — те же, у «Дней» отдельного списка нет
  const view: AdsView = mode === 'breakdowns' ? 'days' : mode;
  const [account, setAccount] = useState('all');
  const [offer, setOffer] = useState('all');
  const [state, setState] = useState<AdsState>('all');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 300);
  const [cols, setCols] = useColumns();
  const { sort, toggle, set: setSort } = useViewSort(view);
  const integ = useIntegrations();
  const filters = {
    period: periodKey(period),
    account: account === 'all' ? '' : account,
    offer: offer === 'all' ? '' : offer,
    state,
    q,
  };
  const r = useAds({ ...filters, view });
  const d = r.data;
  const meta = integ.data?.meta;
  const mark = useMarkForeign();

  const filtered = account !== 'all' || offer !== 'all' || state !== 'all' || text.trim() !== '';
  const reset = () => {
    setAccount('all');
    setOffer('all');
    setState('all');
    setText('');
  };

  // Имена офферов запоминаем: выбранный остаётся в списке, даже если за новый период у него ничего нет
  const offerNames = useRef(new Map<string, string>());
  for (const o of d?.options.offers ?? []) offerNames.current.set(o.id, o.name);
  const offerOpts = useMemo(() => {
    const list = d?.options.offers ?? [];
    const opts: Array<FilterOption<string>> = [
      { value: 'all', label: 'Все офферы', count: list.reduce((t, o) => t + o.count, 0) },
      ...list.map((o) => ({ value: o.id, label: o.name, count: o.count })),
    ];
    if (offer !== 'all' && !list.some((o) => o.id === offer))
      opts.push({ value: offer, label: offerNames.current.get(offer) ?? 'Оффер', count: 0 });
    return opts;
  }, [d, offer]);
  const accountOpts = useMemo(() => {
    const list = d?.options.accounts ?? [];
    return [
      { value: 'all', label: 'Все кабинеты', count: list.reduce((t, a) => t + a.count, 0) },
      ...list.map((a) => ({ value: a.id, label: a.name, count: a.count })),
    ];
  }, [d]);
  const stateOpts = STATES.map((s) => ({ ...s, count: d?.options.states[s.value] }));

  if (meta && !meta.connected && !d?.campaigns.length && !d?.totals.spend)
    return (
      <>
        <PageHead title="Реклама" sub="Расход из Meta обновляется каждые 10 минут" />
        <div className="pc">
          <div className="card">
            <EmptyState
              icon={<PlugZap />}
              title="Подключите рекламный кабинет Meta"
              action={
                <Link to="/settings" hash="meta" className="btn btn-primary">
                  Открыть интеграции
                </Link>
              }
            >
              Платформа будет забирать кампании, объявления и расход каждые 10 минут и сводить их с лидами и
              апрувами. Только чтение: в рекламе ничего не меняется.
            </EmptyState>
          </div>
        </div>
      </>
    );

  return (
    <>
      <PageHead
        title="Реклама"
        sub={
          d?.syncedAt
            ? `Расход из Meta обновлён ${when(d.syncedAt)}`
            : 'Расход из Meta обновляется каждые 10 минут'
        }
      />
      <div className="pc">
        <FilterBar>
          <PeriodPicker value={period} onChange={setPeriod} />
          {accountOpts.length > 2 && (
            <FilterSelect
              label="Кабинет"
              value={account}
              onChange={setAccount}
              options={accountOpts}
              allValue="all"
            />
          )}
          <FilterSelect label="Оффер" value={offer} onChange={setOffer} options={offerOpts} allValue="all" />
          <FilterSelect
            label="Состояние"
            value={state}
            onChange={setState}
            options={stateOpts}
            allValue="all"
            width={240}
          />
          <ResetFilters shown={filtered} onClick={reset} />
          <label className="search-field filter-end">
            <Search aria-hidden />
            <input
              type="search"
              placeholder="Название или ID"
              aria-label="Найти кампанию, адсет или объявление по названию или ID"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <span className="only-m" hidden={mode === 'breakdowns'}>
            <FilterSelect
              label="Сортировка"
              value={sortValue(sort)}
              onChange={(v) => setSort(sortOf(v, sort))}
              options={phoneSorts(view, sort)}
              icon={<ArrowUpDown aria-hidden />}
              width={240}
              showValue
            />
          </span>
        </FilterBar>

        {r.isError && !d ? (
          <div className="card">
            <EmptyState
              icon={<Megaphone />}
              title="Не удалось загрузить рекламу"
              action={<Button onClick={() => r.refetch()}>Повторить</Button>}
            >
              Цифры не пришли с сервера. Проверьте интернет и повторите — если не поможет, спросите агента,
              что с платформой.
            </EmptyState>
          </div>
        ) : (
          <div className={clsx('ads-body', r.isPlaceholderData && 'is-busy')} aria-busy={r.isFetching}>
            <AdsKpis d={d} period={period} />
            {!d ? (
              <>
                <div className="card ads-chart">
                  <Skeleton h={236} />
                </div>
                <div className="card" style={{ padding: 'var(--card-pad)' }}>
                  <div className="flex flex-col gap-3">
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} h={22} />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                {d.days.length > 1 && d.days.some((x) => x.spend > 0 || x.leads > 0) && (
                  <AdsChart days={d.days} />
                )}
                <Coverage d={d} />
                {mode === 'breakdowns' && <Breakdowns filters={filters} period={period} onView={setView} />}
                <AdsTable
                  hidden={mode === 'breakdowns'}
                  d={d}
                  view={view}
                  onView={setView}
                  cols={cols}
                  onCols={setCols}
                  sort={sort}
                  onSort={toggle}
                  onOffer={(id) => {
                    setOffer(id);
                    setView('campaigns');
                  }}
                  onForeign={(id, name) => mark(id, name, true)}
                  busy={r.isPlaceholderData}
                  empty={
                    <NothingHere
                      d={d}
                      period={period}
                      filtered={filtered}
                      onReset={reset}
                      onPeriod={setPeriod}
                    />
                  }
                />
                <AdsAccounts />
                {d.foreign.length > 0 && <Foreign list={d.foreign} period={period} />}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Пусто: под фильтры ничего не подошло, первая загрузка ещё идёт или в эти дни рекламы не было. */
function NothingHere({
  d,
  period,
  filtered,
  onReset,
  onPeriod,
}: {
  d: AdsData;
  period: Period;
  filtered: boolean;
  onReset: () => void;
  onPeriod: (p: Period) => void;
}) {
  if (filtered)
    return (
      <EmptyState
        icon={<SearchX />}
        title="Под фильтры ничего не подошло"
        action={<Button onClick={onReset}>Сбросить фильтры</Button>}
      >
        {upper(periodPhrase(period))} нет объявлений с таким кабинетом, оффером, состоянием или названием.
      </EmptyState>
    );
  if (!d.syncedAt)
    return (
      <EmptyState icon={<Megaphone />} title="Первая загрузка из Meta ещё идёт">
        Обычно это несколько минут: кампании, объявления и расход появятся здесь сами.
      </EmptyState>
    );
  const key = periodKey(period);
  const wider: Period | null =
    key === 'all'
      ? null
      : key === '30d'
        ? { kind: 'preset', preset: 'all' }
        : { kind: 'preset', preset: '30d' };
  return (
    <EmptyState
      icon={<Megaphone />}
      title={`${upper(periodPhrase(period))} рекламы не было`}
      action={
        wider && <Button onClick={() => onPeriod(wider)}>Показать {periodLabel(wider).toLowerCase()}</Button>
      }
    >
      В эти дни не было расхода, лидов и включённых кампаний.
    </EmptyState>
  );
}

/** Сколько лидов связано с объявлениями и почему остальные — нет; кабинеты не по Ташкенту. */
function Coverage({ d }: { d: AdsData }) {
  const lost = d.totals.leads - d.attributedLeads;
  const untagged = d.health.activeAds - d.health.activeTagged;
  const unlinked = d.health.activeAds - d.health.activeLinked;
  const tz = d.tzMismatch ?? [];
  if (lost <= 0 && untagged <= 0 && unlinked <= 0 && d.health.disapproved === 0 && tz.length === 0)
    return null;
  return (
    <div className="ads-notes">
      {tz.map((a) => (
        <p key={a.id} className="warn">
          <TriangleAlert />
          <span>
            Кабинет «{a.name}» живёт по поясу {a.timezone ?? 'не Ташкента'}: его расход по дням не совпадает с
            днями лидов, поэтому CPL и прибыль по дням неточны. Итоги за неделю и больше — почти точные.
          </span>
        </p>
      ))}
      {lost > 0 && (
        <p>
          С объявлениями связаны {int(d.attributedLeads)} из {int(d.totals.leads)} лидов периода. Остальные
          пришли без ID объявления в метках — в строках таблицы их нет, в итогах есть.
        </p>
      )}
      {untagged > 0 && (
        <p className="warn">
          <TriangleAlert />
          <span>
            {untagged} из {d.health.activeAds} активных объявлений{' '}
            {plural(untagged, 'не передаёт', 'не передают', 'не передают')} свой ID в ссылку.{' '}
            <Link to="/settings" hash="meta">
              Как исправить
            </Link>
          </span>
        </p>
      )}
      {unlinked > 0 && (
        <p>
          С роликами из пула связаны {int(d.health.activeLinked)} из {int(d.health.activeAds)} активных
          объявлений. Связь ставится по названию видео или объявления вида «carplay_c1».
        </p>
      )}
      {d.health.disapproved > 0 && (
        <p className="warn">
          <TriangleAlert />
          <span>Отклонено объявлений: {d.health.disapproved}. Причины — в строке объявления.</span>
        </p>
      )}
    </div>
  );
}

/** Отметка «не моя» / «моя»: список пересчитывается сразу, цифры везде — со следующего запроса. */
function useMarkForeign() {
  const qc = useQueryClient();
  return async (id: string, name: string, foreign: boolean) => {
    try {
      await markForeign(id, foreign);
      await qc.invalidateQueries({ queryKey: ['ads'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
      toast(foreign ? `«${name}» больше не учитывается` : `«${name}» снова в учёте`);
    } catch (e) {
      toast((e as Error).message);
    }
  };
}

/** Кампании друга: расход за период виден, но в итоги, бюджет владельца и страховку не входит. */
function Foreign({ list, period }: { list: ForeignCampaign[]; period: Period }) {
  const mark = useMarkForeign();
  const total = list.reduce((t, c) => t + c.spend, 0);
  return (
    <Card
      className="ads-foreign"
      title="Кампании друга — не учитываются"
      action={
        <span className="muted tnum">
          {usd(total)} {periodPhrase(period)}
        </span>
      }
    >
      <p>Их расход не входит в итоги, прибыль, дневной бюджет и страховку. Агент их не трогает.</p>
      <ul>
        {list.map((c) => (
          <li key={c.id}>
            <div className="min-w-0">
              <b>{c.name}</b>
              <span>
                {adStatus(c.effectiveStatus).label}
                {budgetText(c.budget) && ` · ${budgetText(c.budget)}`}
              </span>
            </div>
            <span className="tnum">{usd(c.spend)}</span>
            <Button size="sm" variant="ghost" onClick={() => mark(c.id, c.name, false)}>
              Вернуть в учёт
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
